import { Tooltip, TooltipContent, TooltipTrigger } from "@exopengithub/exokit";
import type { IconProps } from "@tabler/icons-react";
import { clsx } from "clsx";
import {
	useContext,
	type ComponentProps,
	type ReactElement,
	type ReactNode,
	type Ref,
} from "react";
import { NavLink, type To } from "react-router";
import { LinkItem } from "./LinkItem";
import { SidebarContext } from "./SidebarContext";

interface NavItemProps extends ComponentProps<typeof NavLink> {
	icon: ReactElement<IconProps>;
	label: string;
	to: To;
	isActiveOverride?: boolean;
	suffix?: ReactNode;
	ref?: Ref<HTMLAnchorElement>;
}

export const NavItem = ({
	icon,
	to,
	label,
	className,
	isActiveOverride,
	suffix,
	ref,
	...other
}: NavItemProps) => {
	const { open } = useContext(SidebarContext);
	const linkClassName = clsx("block", className);
	if (typeof to === "string" && to.startsWith("http")) {
		// WHY discarded via _-prefix: NavLink-only routing props, invalid on a plain <a>.
		const {
			discover: _discover,
			prefetch: _prefetch,
			reloadDocument: _reloadDocument,
			replace: _replace,
			state: _state,
			preventScrollReset: _preventScrollReset,
			relative: _relative,
			viewTransition: _viewTransition,
			defaultShouldRevalidate: _defaultShouldRevalidate,
			caseSensitive: _caseSensitive,
			end: _end,
			style: _style,
			...anchorProps
		} = other;
		const item = (
			<a
				href={to}
				className={linkClassName}
				aria-label={label}
				ref={ref}
				{...anchorProps}
			>
				<LinkItem
					isActive={false}
					icon={icon}
					label={open ? label : undefined}
					suffix={open ? suffix : undefined}
				/>
			</a>
		);
		if (open) {
			return item;
		}
		return (
			<Tooltip placement="right">
				<TooltipTrigger asChild>{item}</TooltipTrigger>
				<TooltipContent>{label}</TooltipContent>
			</Tooltip>
		);
	}

	const item = (
		<NavLink
			to={to}
			className={linkClassName}
			{...other}
			aria-label={!open ? label : undefined}
			viewTransition
			ref={ref}
		>
			{({ isActive }) => {
				const finalIsActive =
					isActiveOverride !== undefined ? isActiveOverride : isActive;
				return (
					<LinkItem
						isActive={finalIsActive}
						icon={icon}
						label={open ? label : undefined}
						suffix={open ? suffix : undefined}
					/>
				);
			}}
		</NavLink>
	);

	if (open) {
		return item;
	}
	return (
		<Tooltip placement="right">
			<TooltipTrigger asChild>{item}</TooltipTrigger>
			<TooltipContent>{label}</TooltipContent>
		</Tooltip>
	);
};
